import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { useHistory, useParams } from "react-router-dom";
import Cookies from "universal-cookie";
import CardHeader from "../CardHeader/CardHeader";
import CustomTextBox from "../CustomTextBox/CustomTextBox";
import CustomButton from "../CustomButton/CustomButton";
import theme from "../../theme/theme";
import { Auth } from "aws-amplify";
import CustomAlertLabel from "../CustomAlertLabel/CustomAlertLabel";
import CustomModal from "../CustomModal/CustomModal";
import { emailRegex, passwordRegex } from "../../Regex";
import Snackbar from "@mui/material/Snackbar";
import {
  getUserAccountDetails,
  setId,
  setLoginSuccessResponse,
  resetNewDashboardInformation,
  getNewDashboardInformation,
  resetUserAccountDetails,
} from "../../redux/actions";
import LoadingBar from "../LoadingBar/LoadingBar";
import { SET_LOADING } from "../../redux/action-types";
import { analytics } from "../../analytics";

const BodyWrapper = styled.div`
  text-align: left;

  .inputBoxSection {
    margin-top: 7%;
  }

  .buttonContainer {
    text-align: right;
    margin-top: 7%;
  }

  .disclaimer {
    width: 100%;
    text-align: left;
    margin-top: 4%;
    text-decoration: underline;
    cursor: pointer;
  }

  .linkButton {
    text-decoration: underline;
    color: ${theme.colors.primaryStrong};
    font-weight: 700;
    cursor: pointer;
  }
`;

const AdvisorLoginForm = () => {
  const history = useHistory();
  const cookies = new Cookies();
  const dispatch = useDispatch();
  const { isPasswordChanged } = useParams();

  const { userAccountDetails, loading } = useSelector((state) => state.user);

  const [customAlertMessage, setCustomAlertMessage] = useState("");
  const [isLoaded, setIsLoaded] = useState(true);
  const [mailId, setMailId] = useState("");
  const [password, setPassword] = useState("");
  const [customModalProps, setCustomModalProps] = useState({
    isVisible: false,
    headerMessage: "",
    subHeaderMessage: "",
    primaryButtonText: "",
  });
  const [isSnackBarVisible, setisSnackBarVisible] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState("");

  useEffect(() => {
    if (isPasswordChanged === "passwordChanged") {
      setisSnackBarVisible(true);
      setSnackBarMessage("Please login with your new password");
    }
  }, [isPasswordChanged]);

  useEffect(() => {
    if (loading) {
      //setIsLoaded(true);
    }
  }, [loading]);

  const handleCustomModalOnClose = () => {
    setCustomModalProps((state) => ({
      ...state,
      isVisible: false,
    }));
  };

  const handleSignupClick = () => {
    history.push("/signup");
  };

  const handleForgotPasswordClick = () => {
    history.push("/forgot-password");
  };

  const handleCustomTextBoxOnBlur = (textBoxName, value) => {
    if (textBoxName === "mailId") {
      setMailId(value);
    } else if (textBoxName === "password") {
      setPassword(value);
    }
  };

  const handleCustomTextBoxKeyPress = (event) => {
    if (event.key === "Enter") {
      loginUser();
    }
  };

  const loginUser = async () => {
    if (!mailId || !password) {
      setCustomAlertMessage("All the fields are mandatory");
      return;
    }
    dispatch({ type: SET_LOADING, payload: true });
    setCustomAlertMessage("");
    try {
      setIsLoaded(false);
      const successResponse = await Auth.signIn(mailId, password);

      dispatch(resetNewDashboardInformation());
      dispatch(setLoginSuccessResponse(successResponse));

      if (successResponse?.signInUserSession?.accessToken?.jwtToken) {
        cookies.set(
          "accessToken",
          successResponse?.signInUserSession?.accessToken?.jwtToken,
          { path: "/" }
        );

        dispatch(getUserAccountDetails());
      } else if (successResponse?.challengeName == "NEW_PASSWORD_REQUIRED") {
        history.push("/change-default-password");
      } else {
        setIsLoaded(true);
        dispatch({ type: SET_LOADING, payload: false });
        history.push("/multi-factor-authentication");
      }
    } catch (error) {
      dispatch({ type: SET_LOADING, payload: false });
      //error -  UserNotConfirmedException: User is not confirmed.
      //error -  NotAuthorizedException: Incorrect username or password.
      if (error.name === "UserNotConfirmedException") {
        dispatch(setId(mailId, password));
        setCustomModalProps((state) => ({
          ...state,
          isVisible: true,
          headerMessage:
            error.message || "Something went wrong please try again",
          subHeaderMessage: "Please verify your email",
          primaryButtonText: "Verify mail",
        }));
      } else if (error.name === "NotAuthorizedException") {
        setCustomModalProps((state) => ({
          ...state,
          isVisible: true,
          headerMessage:
            error.message || "Something went wrong please try again",
          subHeaderMessage: "Username and password combination is wrong",
          primaryButtonText: "Ok",
        }));
      }
    }
  };

  const handleCustomModalPrimaryButtonClick = () => {
    if (customModalProps.primaryButtonText === "Verify mail")
      history.push(`/mail-verification/${mailId}/fromModal`);
    else
      setCustomModalProps((state) => ({
        ...state,
        isVisible: false,
      }));
  };

  useEffect(() => {
    if (userAccountDetails) {
      analytics.track("Signed In", {
        userId: userAccountDetails?.id || "",
      });
      if (userAccountDetails.userType !== "Investor") {
        history.push(`/advisor-portal-landing`);
      } else {
        dispatch({ type: SET_LOADING, payload: false });
        dispatch(resetUserAccountDetails());
        setCustomModalProps((state) => ({
          ...state,
          isVisible: true,
          headerMessage: "Incorrect username or password",
          subHeaderMessage: "Username and password combination is wrong",
          primaryButtonText: "Ok",
        }));
      }
    }
  }, [userAccountDetails, history, dispatch]);

  return (
    <BodyWrapper>
      {!isLoaded && (
        <LoadingBar isLoading={loading} setIsLoaded={setIsLoaded} />
      )}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={isSnackBarVisible}
        autoHideDuration={3000}
        message={snackBarMessage}
        onClose={() => {
          setisSnackBarVisible(false);
        }}
      />
      {customModalProps.isVisible && (
        <CustomModal
          onClose={handleCustomModalOnClose}
          headerMessage={customModalProps.headerMessage}
          subHeaderMessage={customModalProps.subHeaderMessage}
          primaryButtonText={customModalProps.primaryButtonText}
          onClick={handleCustomModalPrimaryButtonClick}
        />
      )}
      <CardHeader text={"Login"} />
      <div className="inputBoxSection">
        <CustomTextBox
          placeholder="Email"
          errorMessage="Not a valid mail id"
          value={mailId}
          name="mailId"
          regexCode={emailRegex}
          onBlur={handleCustomTextBoxOnBlur}
          onKeyPress={handleCustomTextBoxKeyPress}
        />
      </div>
      <div className="inputBoxSection">
        <CustomTextBox
          placeholder="Password"
          errorMessage="Not a valid password"
          value={password}
          name="password"
          type={"password"}
          regexCode={passwordRegex}
          onBlur={handleCustomTextBoxOnBlur}
          onKeyPress={handleCustomTextBoxKeyPress}
        />
      </div>
      <div className="buttonContainer">
        <CustomButton text={"Login"} onClick={loginUser} />
        <CustomAlertLabel message={customAlertMessage} />
      </div>
      {/* <div className="disclaimer">
        Dont have an account?{" "}
        <span className="linkButton" onClick={handleSignupClick}>
          Signup here
        </span>
      </div>
      <div className="disclaimer">
        Forgot your password?{" "}
        <span className="linkButton" onClick={handleForgotPasswordClick}>
          Reset here
        </span>
      </div> */}
    </BodyWrapper>
  );
};

export default AdvisorLoginForm;
